import { Stream } from '@engoo/realtime-protocol/stream';
import { IIncomingPeerMessage } from '@engoo/realtime-protocol/signaling';
import { IPeerName } from '@engoo/realtime-protocol/peerName';
import { UpdateMessage, UpdateMessageType } from '@engoo/realtime-protocol/signaling/updates';
import {
  IIceCandidateMessage,
  INegotiationNeededMessage,
  ISdpOfferMessage,
  NegotiationMessageType,
  ITrackMids,
} from '@engoo/realtime-protocol/signaling/negotiation';
import { EntityStringId } from 'app/types/entity';
import { IEncodedPersistedAction, IPersistedAction, StreamPosition } from '@engoo/realtime-protocol/persisted';
import { StreamActions, IMetadata, EncodedAction } from 'app/services/rtc/sync/ActionStream';
import { ParticipantType } from 'app/models/rtc/constants';
import { IBotCommandAck, BotCommandArgs } from 'app/services/rtc/BotCommand';
import { BotMessageType } from '@engoo/realtime-protocol/signaling/bot';

/**
 * The types in this file should
 * be moved to realtime-protocol shared library
 */

export enum RoomActions {
  Authorize  = 'authorize',
  Join       = 'join',
  Stats      = 'stats',
  Message    = 'message',
  ChangeName = 'changeName',
}

export enum RoomEvents {
  Redirect = 'redirect',
  Message  = 'message',
  Join     = 'join',
  Remove   = 'remove',
  Stats    = 'stats',
}

export interface IStunServer {
  urls: string[];
}

export interface ITurnServer {
  credential: string;
  urls: string[];
  username: string;
}

export interface IJoinResponse {
  peers: IPeerDetails[];
  timestamp: string;
  stunServers: IStunServer[];
  turnServers: ITurnServer[];
}

export interface IPeerJoinedEvent {
  peer: IPeerDetails;
}

export interface IPeerLeaveEvent {
  id: EntityStringId;
}

export interface IRelayedAction {
  stream: Stream;
  action: IPersistedAction;
}

export interface IRelayedMessage extends IIncomingPeerMessage {
  type: string;
}

export interface IActionStreamStateChanged extends IMetadata {
  stream: Stream;
}

export interface IPeerDetails {
  id: EntityStringId;
  name: IPeerName;
  presence: ParticipantType;
  clientData: unknown;
  appData: unknown;
  isBot: boolean;
}

export interface IAuthorizationResponse {
  result: 'success' | 'redirect';
}

export interface IInsertAction<
  S extends Stream,
  Action extends EncodedAction<S>,
> {
  stream: S;
  position: StreamPosition;
  action: Action;
}

export interface IAppendAction<
  S extends Stream,
  Action extends EncodedAction<S>,
> {
  action: Action;
}

export interface ILockAction<S extends Stream> {
  stream: S;
}

export interface ISubscriptionRequest<Streams extends Stream> {
  streams: ReadonlyArray<{
    name: Streams;
    lastPosition?: number;
  }>;
}

export enum RtcStatsEventType {
  PeerMetadata = 'peerMeta',
  RtcConnectionEvent = 'rtcConnEvent',
  Mids = 'mids',
}

interface IRtcStatsEventBase {
  timestamp: string;
}

interface IRtcStatsConnectionEvent extends IRtcStatsEventBase {
  type: RtcStatsEventType.RtcConnectionEvent;
  rtcConnId: number;
  eventType: string;
  details: unknown
}

export interface IRtcStatsMidsEvent extends IRtcStatsEventBase {
  type: RtcStatsEventType.Mids;
  rtcConnId: number;
  localMids: ITrackMids;
  remoteMids: ITrackMids;
}

interface IRtcStatsPeerEvent extends IRtcStatsEventBase {
  type: RtcStatsEventType.PeerMetadata;
  metadata: {
    userAgent: string;
  };
}

export type RtcStatsEvent =
  | IRtcStatsPeerEvent
  | IRtcStatsConnectionEvent
  | IRtcStatsMidsEvent;

export type RequestedStreams<
  Streams extends readonly unknown[]
> = Streams extends ReadonlyArray<{
  name: infer R;
  lastPosition?: number;
}> ? R : never;

export interface ISubscriptionResponse<SubscribedStreams extends Stream> {
  streams: {
    [S in SubscribedStreams]: {
      baseState: null;
      deltaActions: Array<EncodedAction<S> & IPersistedAction>;
      metadata: IMetadata;
    }
  }
}

export interface IStreamActionWithPayloadMap<S extends Stream, P extends EncodedAction<S>> {
  [StreamActions.INSERT]: IInsertAction<S, P>;
  [StreamActions.APPEND]: IAppendAction<S, P>;
}

export interface IStreamActionWithPayloadReturnTypes<S extends Stream> {
  [StreamActions.INSERT]: EncodedAction<S> & IEncodedPersistedAction;
  [StreamActions.APPEND]: EncodedAction<S> & IEncodedPersistedAction;
}

export interface IRoomActionsPayload {
  [RoomActions.Authorize]: IAuthorizePayload;
  [RoomActions.Join]: unknown;
  [RoomActions.Stats]: unknown;
  [RoomActions.Message]: unknown;
  [RoomActions.ChangeName]: INameChangePayload;
}

export interface IRoomActionsReturnTypes {
  [RoomActions.Authorize]: IAuthorizationResponse;
  [RoomActions.Join]: IJoinResponse;
  [RoomActions.Stats]: RtcStatsEvent;
  [RoomActions.Message]: IBotCommandAck;
  [RoomActions.ChangeName]: null;
}

type StripPayloadType<T extends { type: unknown }> =
  Omit<T, 'type'>;

export interface IMessagePayload {
  [NegotiationMessageType.NegotiationNeeded]: StripPayloadType<INegotiationNeededMessage>;
  [NegotiationMessageType.SdpOffer]: StripPayloadType<ISdpOfferMessage>;
  [NegotiationMessageType.IceCandidate]: StripPayloadType<IIceCandidateMessage>;
  [UpdateMessageType.MediaStatus]: StripPayloadType<UpdateMessage>;
  [BotMessageType.Command]: BotCommandArgs;
}

export interface IAuthorizePayload {
  roomId: EntityStringId;
  token: string;
  clientVersion: number;
  actionVersion: number;
}

interface INameChangePayload {
  name: string;
}
